import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { createSection, updateSection, deleteSection, getAllSections } from '../../api/intranet/sectionsApi';
import { createCard, updateCard, deleteCard, reorderCards } from '../../api/intranet/cardsApi';
import Head from '../../layout/head/Head';
import Content from '../../layout/content/Content';
import {
  Block,
  BlockDes,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  BlockHead,
  Icon,
  PreviewAltCard,
  Button
} from '../../components/Component';
import { Spinner, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { toast } from 'react-toastify';
import './IntranetPage.css';
import SectionEditModal from './components/SectionEditModal';
import CardEditModal from './components/CardEditModal';
import IntranetSection from './components/IntranetSection';
import { showToast } from '../../utils/toast/toast';
import { useSelector } from 'react-redux';
import { newRoles } from '../../utils/envConfig';

const IntranetPage = () => {
  const axios = useAxiosPrivate();
  const queryClient = useQueryClient();

  const authUser = useSelector((state) => state?.user?.loggedInUser);
  const isAdmin = [newRoles.Admin, newRoles.MasterAdmin].includes(authUser?.role);

  const [sections, setSections] = useState([]);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'section' or 'card'

  // Fetch Sections Query
  const { isLoading: isLoadingSections, error: fetchError } = useQuery(
    ['intranet-sections'],
    async () => {
      const sectionsData = await getAllSections(axios);
      setSections(sectionsData);
      return sectionsData;
    },
    {
      refetchOnWindowFocus: true,
      refetchOnMount: false,
      keepPreviousData: true,
      onSuccess: (data) => {
        // Sort sections by order
        const sortedSections = [...data].sort((a, b) => (a.order || 0) - (b.order || 0));
        setSections(sortedSections);
      }
    }
  );

  // Create Section Mutation
  const createSectionMutation = useMutation((newSectionData) => createSection(axios, newSectionData), {
    onSuccess: () => {
      queryClient.invalidateQueries('intranet-sections');
      showToast('Section created successfully!', 'success', 3000);
      setShowSectionModal(false);
      setCurrentSection(null);
    },
    onError: (error) => {
      showToast(`Error creating section: ${error.message || 'An unexpected error occurred.'}`, 'error', 3000);
    }
  });

  // Update Section Mutation
  const updateSectionMutation = useMutation(
    ({ section_id, updateData }) => updateSection(axios, section_id, updateData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('intranet-sections');
        showToast('Section updated successfully!', 'success', 3000);
        setShowSectionModal(false);
        setCurrentSection(null);
      },
      onError: (error) => {
        toast.error(`Error updating section: ${error.message || 'An unexpected error occurred.'}`);
      }
    }
  );

  // Delete Section Mutation
  const deleteSectionMutation = useMutation((section_id) => deleteSection(axios, section_id), {
    onSuccess: () => {
      queryClient.invalidateQueries('intranet-sections');
      showToast('Section deleted successfully!', 'success', 3000);
      setShowDeleteConfirmModal(false);
      setItemToDelete(null);
    },
    onError: (error) => {
      showToast(`Error deleting section: ${error.message || 'An unexpected error occurred.'}`, 'error', 3000);
      setShowDeleteConfirmModal(false);
      setItemToDelete(null);
    }
  });

  // Create Card Mutation
  const createCardMutation = useMutation((newCardData) => createCard(axios, newCardData), {
    onSuccess: () => {
      queryClient.invalidateQueries('intranet-sections');
      showToast('Card created successfully!', 'success', 3000);
      setShowCardModal(false);
      setCurrentCard(null);
    },
    onError: (error) => {
      showToast(`Error creating card: ${error.message || 'An unexpected error occurred.'}`, 'error', 3000);
    }
  });

  // Update Card Mutation
  const updateCardMutation = useMutation(({ cardId, updateData }) => updateCard(axios, cardId, updateData), {
    onSuccess: () => {
      queryClient.invalidateQueries('intranet-sections');
      showToast('Card updated successfully!', 'success', 3000);
      setShowCardModal(false);
      setCurrentCard(null);
    },
    onError: (error) => {
      showToast(`Error updating card: ${error.message || 'An unexpected error occurred.'}`, 'error', 3000);
    }
  });

  // Delete Card Mutation
  const deleteCardMutation = useMutation((cardId) => deleteCard(axios, cardId), {
    onSuccess: () => {
      queryClient.invalidateQueries('intranet-sections');
      showToast('Card deleted successfully!', 'success', 3000);
      setShowDeleteConfirmModal(false);
      setItemToDelete(null);
    },
    onError: (error) => {
      showToast(`Error deleting card: ${error.message || 'An unexpected error occurred.'}`, 'error', 3000);
      setShowDeleteConfirmModal(false);
      setItemToDelete(null);
    }
  });

  // Reorder Cards Mutation
  const reorderCardsMutation = useMutation(
    ({ sectionId, cardsWithOrder }) => reorderCards(axios, sectionId, { cards: cardsWithOrder }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('intranet-sections');
        showToast('Cards reordered successfully!', 'success', 3000);
      },
      onError: (error) => {
        showToast(`Error reordering cards: ${error.message || 'An unexpected error occurred.'}`, 'error', 3000);
      }
    }
  );

  // Drag and Drop Handler
  const handleOnDragEnd = useCallback(
    async (result) => {
      const { destination, source, draggableId } = result;

      if (!destination) {
        return;
      }

      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return;
      }

      // Handle section drag and drop only
      const newSections = Array.from(sections);
      const [reorderedSection] = newSections.splice(source.index, 1);
      newSections.splice(destination.index, 0, reorderedSection);

      // Update the order property for each section
      const updatedSections = newSections.map((section, index) => ({
        ...section,
        order: index
      }));

      setSections(updatedSections);

      // Update the section order in the backend
      try {
        await updateSection(axios, reorderedSection.id, { order: destination.index });
        queryClient.invalidateQueries('intranet-sections');
        showToast('Section reordered successfully!', 'success', 3000);
      } catch (error) {
        showToast('Error updating section order', 'error', 3000);
        // Revert the change
        queryClient.invalidateQueries('intranet-sections');
      }
    },
    [sections, axios, queryClient]
  );

  // Modal Handlers
  const handleOpenCreateSectionModal = () => {
    setCurrentSection(null);
    setShowSectionModal(true);
  };

  const handleEditSection = (section) => {
    setCurrentSection(section);
    setShowSectionModal(true);
  };

  const handleDeleteSection = (section) => {
    setItemToDelete(section);
    setDeleteType('section');
    setShowDeleteConfirmModal(true);
  };

  const handleOpenCreateCardModal = (section_id) => {
    setCurrentCard({ section_id });
    setShowCardModal(true);
  };

  const handleEditCard = (card) => {
    setCurrentCard(card);
    setShowCardModal(true);
  };

  const handleDeleteCard = (card) => {
    setItemToDelete(card);
    setDeleteType('card');
    setShowDeleteConfirmModal(true);
  };

  const handleSaveSection = (sectionData) => {
    if (sectionData.id) {
      updateSectionMutation.mutate({ section_id: sectionData.id, updateData: sectionData });
    } else {
      createSectionMutation.mutate({ ...sectionData, order: sections.length });
    }
  };

  const handleSaveCard = (cardData) => {
    if (cardData.id) {
      updateCardMutation.mutate({ cardId: cardData.id, updateData: cardData });
    } else {
      createCardMutation.mutate(cardData);
    }
  };

  const handleReorderCards = (sectionId, cardsWithOrder) => {
    reorderCardsMutation.mutate({ sectionId, cardsWithOrder });
  };

  const confirmDelete = () => {
    if (deleteType === 'section' && itemToDelete) {
      deleteSectionMutation.mutate(itemToDelete.id);
    } else if (deleteType === 'card' && itemToDelete) {
      deleteCardMutation.mutate(itemToDelete.id);
    }
  };

  if (isLoadingSections && sections.length === 0) {
    return (
      <Content>
        <div className="text-center p-5">
          <Spinner color="primary" />
          <p>Loading intranet sections...</p>
        </div>
      </Content>
    );
  }

  if (fetchError) {
    return (
      <Content>
        <PreviewAltCard>
          <p className="text-danger text-center">Error fetching intranet data: {fetchError.message}</p>
        </PreviewAltCard>
      </Content>
    );
  }

  return (
    <React.Fragment>
      <Head title="Intranet" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Intranet
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>Manage your intranet sections and cards. You have {sections.length} sections.</p>
              </BlockDes>
            </BlockHeadContent>
            {isAdmin && <BlockHeadContent>
              <Button color="primary" onClick={handleOpenCreateSectionModal}>
                <Icon name="plus"></Icon>
                <span>Add New Section</span>
              </Button>
            </BlockHeadContent>}
          </BlockBetween>
        </BlockHead>

        <Block>
          {sections.length === 0 ? (
            <PreviewAltCard>
              <div className="text-center p-5">
                <Icon name="layers" style={{ fontSize: '3rem', color: '#e5e9f2' }}></Icon>
                <h5 className="mt-3">No sections found</h5>
                <p className="text-soft">
                    {isAdmin ? "Create your first section to get started with the intranet." : "Contact administrators to create sections."}</p>
                {isAdmin && <Button color="primary" onClick={handleOpenCreateSectionModal}>
                  <Icon name="plus"></Icon>
                  <span>Add First Section</span>
                </Button>}
              </div>
            </PreviewAltCard>
          ) : (
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="sections-list">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {sections.map((section, index) => (
                      <Draggable key={section.id} draggableId={section.id.toString()} index={index} isDragDisabled={!isAdmin}>
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps} className="mb-4">
                            <IntranetSection
                              section={section}
                              dragHandleProps={provided.dragHandleProps}
                              onEditSection={handleEditSection}
                              onDeleteSection={handleDeleteSection}
                              onCreateCard={handleOpenCreateCardModal}
                              onEditCard={handleEditCard}
                              onDeleteCard={handleDeleteCard}
                              onReorderCards={handleReorderCards}
                              isAdminUser={isAdmin}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Block>
      </Content>

      {/* Section Edit Modal */}
      <SectionEditModal
        show={showSectionModal}
        onHide={() => setShowSectionModal(false)}
        section={currentSection}
        onSave={handleSaveSection}
        isLoading={createSectionMutation.isLoading || updateSectionMutation.isLoading}
      />

      {/* Card Edit Modal */}
      <CardEditModal
        show={showCardModal}
        onHide={() => setShowCardModal(false)}
        card={currentCard}
        onSave={handleSaveCard}
        isLoading={createCardMutation.isLoading || updateCardMutation.isLoading}
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirmModal} toggle={() => setShowDeleteConfirmModal(false)}>
        <ModalHeader toggle={() => setShowDeleteConfirmModal(false)}>Confirm Delete</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this {deleteType}?{' '}
          <strong>{itemToDelete?.name || itemToDelete?.title}</strong>? This action cannot be undone.
          {deleteType === 'section' && itemToDelete?.cards?.length > 0 && (
            <p className="text-warning mt-2">
              <Icon name="alert-circle"></Icon> This section contains {itemToDelete.cards.length} card(s) that will also
              be deleted.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowDeleteConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={confirmDelete}
            disabled={deleteSectionMutation.isLoading || deleteCardMutation.isLoading}
          >
            {deleteSectionMutation.isLoading || deleteCardMutation.isLoading ? <Spinner size="sm" /> : 'Confirm Delete'}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default IntranetPage;
